import { ProductVariantSerializer } from "@app-contexts/public/features/products/data/types/products";
import Tag from "@ui-kit/data-display/Tag/Tag.web";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import Heading from "@ui-kit/typography/Heading/Heading.web";
import Form from "@shared/components/ui-kit/form/Form/Form.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import Link from "@shared/components/ui-kit/navigation/Link/Link.web";
import { enumsTypes } from "@shared/constants";
import { DEFAULT_MAX_TICKET_PURCHASE_LIMIT } from "@shared/constants/common";
import { PURCHASE_SOURCE_PLATFORM_MAP } from "@shared/constants/enums";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import useRedirect from "@shared/hooks/useRedirect.web";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import PWYCTicketType from "@shared/modules/events/components/tickets/pay-what-you-can/PWYCTicketType/PWYCTicketType.web";
import { PayWhatYouCanConfiguration } from "@shared/modules/events/types";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import { FeesPricingInfoType } from "@shared/modules/financials/types";
import {
  getItemPrice,
  getPriceDifference,
} from "@shared/modules/financials/utils/price-utils";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { componentName } from "@shared/utils/bem";
import url from "@shared/utils/urls/url";
import StatusBadge from "@ui-application/data-display/badges/StatusBadge/StatusBadge.web";
import ProductVariantSelect from "@ui-application/form/ProductVariantSelect/ProductVariantSelect.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import QuantitySelectDropdown from "@ui-application/form/QuantitySelectDropdown/QuantitySelectDropdown.web";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";

export interface ItemQuantitySelectInfoProps {
  id?: number;
  alwaysBelow?: boolean;
  itemTitle?: string;
  itemSubtitle?: string;
  itemPrice?: string;
  currency: enumsTypes.CurrencyMapIdType;
  feesPricingInfo?: FeesPricingInfoType;
  hasAssignedSeating?: boolean;
  hasAllInPricing?: boolean;
  customPackageData?: {
    hasPackage: boolean;
    canEdit: boolean;
    quantity: number;
  };
  soldOut?: boolean;
  minSelectionAmount?: number;
  maxSelectionAmount?: number;
  amountSelected: number;
  onAmountSelectedChange?: (amount: number) => void;
  openFrontendDetailsUrl?: (opts?: { ticketTypeId?: number }) => void;
  isLoading: boolean;
  productVariantData?: {
    productVariants: ProductVariantSerializer[];
    setProductVariants: Dispatch<SetStateAction<ProductVariantSerializer[]>>;
    selectedProductVariant: ProductVariantSerializer;
    setSelectedProductVariant: Dispatch<
      SetStateAction<ProductVariantSerializer>
    >;
  };
  itemImage?: string;
  livestreamAccess?: boolean;
  inventoryComponent?: JSX.Element;
  showQuantityButtons?: boolean;
  isCalendar?: boolean;
  useServerlessResizer?: boolean;
  isWaitlistTicket?: boolean;
  frontendDetailsUrl?: string;
  autoDiscount?: {
    description: string;
    amount: number;
  } | null;
  isOnSale?: boolean;
  /**
   * For TTs with patron_type.
   *
   * When true, we display the UI a bit differently
   */
  isPatronItemSelection?: boolean;
  /**
   * For TTs with patron_type.
   *
   * A starting from price that we compare _this_ tt's price to
   * in order to display a price difference.
   *
   * Requires `isPatronItemSelection` to be true.
   */
  startingFromPrice?: string;
  /**
   * The number of ticket types that have a patron type.
   */
  ticketTypesPatronTypeCount?: number;
  pwycConfig?: PayWhatYouCanConfiguration | null;
  onSetPwycPrice?: Dispatch<SetStateAction<string | undefined>>;
  pwycPrice?: string;
  forceQuantityButtons?: boolean;
  isPackageParent?: boolean;
  children?: React.ReactNode;
  itemTitleAccessory?: React.ReactNode;
  isModal?: boolean;
  onEventSelectCallback?: () => void;
}

const ItemQuantitySelectInfo: React.FC<ItemQuantitySelectInfoProps> = ({
  id = undefined,
  itemTitle = null,
  itemSubtitle = null,
  itemPrice = null,
  feesPricingInfo = null,
  currency,
  alwaysBelow = false,
  hasAssignedSeating = null,
  hasAllInPricing = null,
  customPackageData = undefined,
  soldOut = null,
  minSelectionAmount = null,
  maxSelectionAmount = null,
  amountSelected,
  onAmountSelectedChange = null,
  openFrontendDetailsUrl = null,
  isLoading,
  itemImage = null,
  livestreamAccess = false,
  inventoryComponent = null,
  showQuantityButtons = true,
  productVariantData = null,
  isCalendar = false,
  useServerlessResizer = false,
  isWaitlistTicket = false,
  frontendDetailsUrl = undefined,
  autoDiscount = { description: "", amount: 0 },
  isOnSale,
  isPatronItemSelection,
  startingFromPrice,
  ticketTypesPatronTypeCount,
  pwycConfig,
  onSetPwycPrice,
  pwycPrice,
  forceQuantityButtons = false,
  isPackageParent,
  children,
  itemTitleAccessory,
  isModal = false,
  onEventSelectCallback,
}) => {
  const { t } = useTranslation("purchases");
  const [imageLoaded, setImageLoaded] = useState(false);
  const { FLAGS } = useDjangoWaffle();
  const { redirect } = useRedirect();

  const openSeatingMap = useCallback(() => {
    // If onEventSelectCallback callback is provided (e.g., in widget context), use it instead of redirecting
    if (onEventSelectCallback) {
      onEventSelectCallback();
      return;
    }
    // frontend_details_url handles both membership and event routes
    const seatingRoute = [frontendDetailsUrl ?? url(), "seating"].join("");
    redirect(seatingRoute);
  }, [frontendDetailsUrl, redirect]);

  const { isBasketUpdating, paymentType, purchaseSourcePlatform } =
    useBasketService();

  const skeletonText = "skeletonText";

  // Prevents alt text from being displayed while image loads
  const getImageVisibility = () => {
    return imageLoaded ? "visible" : "hidden";
  };

  const getOpacity = () => {
    return soldOut && !isWaitlistTicket ? "0.3" : "1.0";
  };

  /**
   * Displays an optional icon button next to the quantity selectors/buttons.
   * Example: an edit button for editing custom packages.
   */
  const getIconDisplay = () => {
    let returnElement = <></>;

    if (customPackageData?.canEdit) {
      returnElement = (
        <Tooltip hasArrow placement="top" label={t("Edit Package")}>
          <IconButton.Ghost
            iconName="Edit"
            iconColor=""
            iconSize="24px"
            disabled={isLoading}
            iconOutline={false}
            marginRight="xs"
            aria-label={t("Edit Package")}
            onClick={() => {
              onAmountSelectedChange?.(customPackageData?.quantity);
            }}
          />
        </Tooltip>
      );
    }

    return returnElement;
  };

  /**
   * Displays quantity selectors or buttons for adding/removing tickets to the cart.
   * Also displays buttons for redirecting users to the correct pages.
   * Examples: redirecting to the seating map or custom package pages.
   */
  const getQuantityButtonDisplay = () => {
    let returnElement = <></>;

    // shows a button that redirects to the seating map page
    // example: on the attraction page calendar modal
    if (hasAssignedSeating && !forceQuantityButtons) {
      returnElement = (
        <Button
          data-testid="item-quantity-select-info-view-seats"
          width="100%"
          colorPalette="primary"
          variant="outline"
          onClick={openSeatingMap}
        >
          <Text fontSize={{ base: "sm", sm: "md" }}>{t("View Seats")}</Text>
        </Button>
      );
    }
    // Waitlisted ticket types on the widget need to redirect to showpass.com.
    // This is because we currently force guest checkout on widgets, but we require a User account for waitlists.
    // We check for sold out because non-sold-out ticket types can continue to display the quantity buttons.
    else if (
      soldOut &&
      isWaitlistTicket &&
      getEmbeddedPurchasePlatform() ===
        PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WIDGET.id
    ) {
      returnElement = (
        <Link
          href={frontendDetailsUrl ?? url()}
          padding={0}
          external
          target="_top"
        >
          <Button
            data-testid="item-quantity-select-info-view-seats"
            width="100%"
            colorPalette="primary"
            variant="solid"
            rightIcon={
              <Icon.LeavePage outline width="element.md" height="element.md" />
            }
          >
            <Text fontSize={{ base: "sm", sm: "md" }}>
              {t("View Waitlist")}
            </Text>
          </Button>
        </Link>
      );
    }
    // Handle the Angular custom package case separately (will be deprecated)
    else if (
      customPackageData?.hasPackage &&
      !FLAGS?.use_new_custom_package_page
    ) {
      returnElement = (
        <Button
          data-testid="item-quantity-select-info-custom-package"
          width="100%"
          colorPalette="primary"
          variant="outline"
          onClick={() => {
            if (openFrontendDetailsUrl) {
              openFrontendDetailsUrl({ ticketTypeId: id });
            }
          }}
        >
          <Text fontSize={{ base: "sm", sm: "md" }}>
            {customPackageData?.canEdit ? t("Edit Package") : t("View Package")}
          </Text>
        </Button>
      );
    } else if (customPackageData?.hasPackage) {
      returnElement = (
        <QuantitySelectDropdown
          id={itemTitle ?? ""}
          minSelectionAmount={minSelectionAmount}
          maxSelectionAmount={maxSelectionAmount}
          amountSelected={amountSelected}
          onAmountSelectedChange={onAmountSelectedChange}
          isQuantityLoading={isBasketUpdating}
          isCalendar={isCalendar}
          isModal={isModal}
        />
      );
      // TODO visual note when minimum selection amount !== 0, as it might cause confusion
      // if difference between min and max selection amount is greater than threshold, use dropdown
    } else {
      returnElement = (
        <Form.Incrementer
          value={amountSelected}
          onChange={(value) => onAmountSelectedChange?.(value)}
          min={minSelectionAmount ?? 0}
          max={maxSelectionAmount ?? DEFAULT_MAX_TICKET_PURCHASE_LIMIT}
          isDisabled={isBasketUpdating}
          isLoading={isBasketUpdating}
          size="md"
          width={{ base: "100%", md: alwaysBelow ? "100%" : "auto" }} // full width when shown on a new line. see quantity-select-below
        />
      );
    }

    return returnElement;
  };

  const displayItemTitleHeading =
    Boolean(itemTitle) &&
    // display item title if it is NOT a patron item selection OR if it is a patron item selection but there is only one patron type.
    // Note that this change is effectively for when the TT name is actually a patron type name.
    // If the patron types represent patrons, and the ticket types represent ticket types, this may not work as well.
    (!isPatronItemSelection ||
      (isPatronItemSelection && ticketTypesPatronTypeCount === 1));
  const displayItemSubtitleHeading = Boolean(itemSubtitle);
  const displayTitleHeadings =
    displayItemTitleHeading || displayItemSubtitleHeading;
  const displayProductVariant = Boolean(productVariantData);
  const displayTitleHeadingsAndProductVariant =
    displayTitleHeadings || displayProductVariant;

  const bem = componentName("item-quantity-select-info");

  return (
    <Box className={bem.className()}>
      {soldOut && !isWaitlistTicket && (
        <Box width="element.6xl" mb="xs">
          <StatusBadge
            status="error"
            className={bem.element("badge", {
              soldOut: true,
            })}
          >
            <Text fontSize="xs" textTransform="uppercase">
              {t("Sold Out")}
            </Text>
          </StatusBadge>
        </Box>
      )}
      {isLoading && (
        <>
          <Skeleton>
            <Text
              as="h2"
              fontSize={{
                base: "md",
                sm: "3xl",
              }}
              fontWeight="bold"
            >
              {skeletonText}
            </Text>
          </Skeleton>
          <Skeleton margin="10px 0px" width="30%">
            <Text
              as="h2"
              fontSize={{
                base: "md",
                sm: "3xl",
              }}
              fontWeight="bold"
            >
              {skeletonText}
            </Text>
          </Skeleton>

          <Flex alignItems="center" justifyContent="space-between">
            <Skeleton>
              <Flex direction="column">
                <Text
                  fontSize={{
                    base: "sm",
                    sm: "lg",
                  }}
                  fontWeight="bold"
                >
                  <CurrencyFormat
                    value={getItemPrice(
                      itemPrice,
                      hasAllInPricing,
                      feesPricingInfo,
                      paymentType,
                      purchaseSourcePlatform
                    )}
                    displayType="text"
                    currency={currency}
                  />
                  {` ${currency}`}
                </Text>
                <AllInPricing
                  hasAllInPricing={Boolean(hasAllInPricing)}
                  feesPricingInfo={feesPricingInfo}
                  price={itemPrice}
                  currency={currency}
                />
              </Flex>
            </Skeleton>
            <Box minWidth="element.2xl" />
            <Skeleton>
              <Flex>{getQuantityButtonDisplay()}</Flex>
            </Skeleton>
          </Flex>
          <Skeleton mt="xs">
            {/* isSmallerThanSm */}
            <Box
              display={{
                base: "none",
                sm: "block",
              }}
            >
              <ReadMore
                descriptionHtml={skeletonText}
                openReadMoreText={t("Show Description")}
                closeReadMoreText={t("Hide Description")}
              />
            </Box>
            {/* !isSmallerThanSm */}
            <Box
              display={{
                base: "block",
                sm: "none",
              }}
            >
              <ReadMore
                descriptionHtml={skeletonText}
                openReadMoreText={t("Read More")}
                closeReadMoreText={t("Show Less")}
              />
            </Box>
          </Skeleton>
        </>
      )}

      {!isLoading && (
        <>
          {(!soldOut || (soldOut && isWaitlistTicket)) && (
            <Flex
              alignItems="center"
              gap="md"
              mb={
                inventoryComponent ||
                livestreamAccess ||
                isWaitlistTicket ||
                customPackageData?.hasPackage
                  ? "10px"
                  : "0px"
              }
            >
              {/* Don't display inventory if TT is sold out and it is now waitlisted */}
              {!!inventoryComponent && !(soldOut && isWaitlistTicket) && (
                <>
                  {React.cloneElement(inventoryComponent, {
                    className: bem.element("badge", {
                      inventory: true,
                    }),
                  })}
                </>
              )}
              {isPackageParent && (
                <StatusBadge
                  status="neutral"
                  className={bem.element("badge", {
                    package: true,
                  })}
                >
                  <Text textTransform="uppercase" size="xs">
                    {t("Package")}
                  </Text>
                </StatusBadge>
              )}
              {livestreamAccess && (
                <StatusBadge
                  status="neutral"
                  className={bem.element("badge", {
                    livestream: true,
                  })}
                >
                  <Text textTransform="uppercase" size="xs">
                    {t("Livestream Access")}
                  </Text>
                </StatusBadge>
              )}
              {soldOut && isWaitlistTicket && (
                <Tag
                  fontSize="sm"
                  fontWeight="bold"
                  backgroundColor="primary.100"
                  className={bem.element("badge", {
                    waitlist: true,
                  })}
                  color="primary.600"
                >
                  <Text size="xs" textTransform="uppercase">
                    {t("Waitlist", { ns: "common" })}
                  </Text>
                </Tag>
              )}
            </Flex>
          )}
          <Box opacity={getOpacity()} flexGrow={1}>
            <Flex
              w="full"
              direction={{ base: "column", md: "row" }}
              h="100%"
              gap="md"
            >
              {itemImage && (
                <CDNImage
                  width={144}
                  height={144}
                  src={itemImage}
                  alt={t("item image")}
                  style={{
                    borderRadius: "8px",
                    visibility: getImageVisibility(),
                  }}
                  onLoad={() => {
                    if (!imageLoaded) {
                      setImageLoaded(true);
                    }
                  }}
                  useServerlessResize={useServerlessResizer}
                />
              )}

              <Flex
                flexDirection="column"
                gap="md"
                width="100%"
                maxWidth="100%"
                className={bem.element("inner-container")}
              >
                {displayTitleHeadingsAndProductVariant && (
                  <Flex alignItems="center" justifyContent="space-between">
                    {displayTitleHeadings && (
                      <Box
                        width="100%"
                        maxWidth="100%"
                        className={bem.element("title-container")}
                      >
                        {displayItemTitleHeading && (
                          <Heading
                            className={bem.element("title")}
                            size={
                              ticketTypesPatronTypeCount === 1 ? "lg" : "2xl"
                            }
                          >
                            {itemTitle}
                          </Heading>
                        )}
                        {itemTitleAccessory}
                        {displayItemSubtitleHeading && (
                          <Text size="lg" color="gray.600">
                            {itemSubtitle}
                          </Text>
                        )}
                      </Box>
                    )}

                    {displayProductVariant &&
                      // solely for type narrowing
                      productVariantData && (
                        <Box>
                          <ProductVariantSelect
                            productVariants={productVariantData.productVariants}
                            selectedProductVariant={
                              productVariantData.selectedProductVariant
                            }
                            setSelectedProductVariant={
                              productVariantData.setSelectedProductVariant
                            }
                          />
                        </Box>
                      )}
                  </Flex>
                )}

                {pwycConfig &&
                itemPrice &&
                onSetPwycPrice &&
                showQuantityButtons &&
                FLAGS.use_pwyc_configuration ? (
                  <PWYCTicketType
                    defaultPrice={getItemPrice(
                      itemPrice,
                      Boolean(hasAllInPricing),
                      undefined,
                      paymentType,
                      purchaseSourcePlatform
                    )}
                    quantityButtonsComponent={getQuantityButtonDisplay()}
                    pwycConfig={pwycConfig}
                    price={pwycPrice}
                    onSetPrice={onSetPwycPrice}
                    currency={currency}
                    isWaitlisted={isWaitlistTicket}
                  />
                ) : (
                  <>
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      className={bem.element("price-container")}
                    >
                      <Flex direction="column">
                        <Heading as="h3" size="lg">
                          <Flex direction="row">
                            {/* ORIGINAL PRICE */}
                            {itemPrice && !startingFromPrice && (
                              <Text
                                textDecoration={
                                  (autoDiscount?.amount ?? 0) > 0 &&
                                  !soldOut &&
                                  isOnSale
                                    ? "line-through"
                                    : "none"
                                }
                              >
                                <CurrencyFormat
                                  value={getItemPrice(
                                    itemPrice,
                                    Boolean(hasAllInPricing),
                                    feesPricingInfo,
                                    paymentType,
                                    purchaseSourcePlatform
                                  )}
                                  displayType="text"
                                  currency={currency}
                                  includeSuffix={autoDiscount?.amount === 0}
                                />
                              </Text>
                            )}
                            {/* WITH STARTING FROM PRICE */}
                            {itemPrice &&
                              startingFromPrice &&
                              ticketTypesPatronTypeCount && (
                                <>
                                  {ticketTypesPatronTypeCount > 1 ? (
                                    <Flex flexDirection="column" gap="2xs">
                                      <Text fontSize="md">{itemTitle}</Text>
                                      <Text
                                        textDecoration={
                                          (autoDiscount?.amount ?? 0) > 0 &&
                                          !soldOut &&
                                          isOnSale
                                            ? "line-through"
                                            : "none"
                                        }
                                        fontSize="sm"
                                        color="gray.600"
                                        fontWeight={400}
                                        display={
                                          getPriceDifference(
                                            getItemPrice(
                                              itemPrice,
                                              false,
                                              feesPricingInfo,
                                              paymentType,
                                              purchaseSourcePlatform
                                            ),
                                            startingFromPrice
                                          ) > 0
                                            ? "inline-block"
                                            : "none"
                                        }
                                      >
                                        <Text as="span">
                                          {getPriceDifference(
                                            getItemPrice(
                                              itemPrice,
                                              false,
                                              feesPricingInfo,
                                              paymentType,
                                              purchaseSourcePlatform
                                            ),
                                            startingFromPrice
                                          ) >= 0 ? (
                                            <>&#x2B;</>
                                          ) : (
                                            <>&minus;</>
                                          )}
                                        </Text>{" "}
                                        <CurrencyFormat
                                          value={getPriceDifference(
                                            getItemPrice(
                                              itemPrice,
                                              false,
                                              feesPricingInfo,
                                              paymentType,
                                              purchaseSourcePlatform
                                            ),
                                            startingFromPrice
                                          )}
                                          displayType="text"
                                          currency={currency}
                                          includeSuffix={
                                            autoDiscount?.amount === 0
                                          }
                                        />
                                      </Text>
                                    </Flex>
                                  ) : (
                                    <Flex flexDirection="column" gap="md">
                                      <Box>
                                        <Text
                                          fontSize="sm"
                                          color="gray.600"
                                          fontWeight={400}
                                        >
                                          {t("Prices from")}
                                        </Text>
                                        <Text
                                          textDecoration={
                                            (autoDiscount?.amount ?? 0) > 0 &&
                                            !soldOut &&
                                            isOnSale
                                              ? "line-through"
                                              : "none"
                                          }
                                          fontSize="md"
                                        >
                                          <CurrencyFormat
                                            value={getItemPrice(
                                              itemPrice,
                                              Boolean(hasAllInPricing),
                                              feesPricingInfo,
                                              paymentType,
                                              purchaseSourcePlatform
                                            )}
                                            displayType="text"
                                            currency={currency}
                                            includeSuffix={
                                              autoDiscount?.amount === 0
                                            }
                                          />
                                        </Text>
                                      </Box>
                                    </Flex>
                                  )}
                                </>
                              )}
                            {/* AUTO DISCOUNT PRICE */}
                            {itemPrice &&
                              Number(itemPrice) > 0 &&
                              (autoDiscount?.amount ?? 0) > 0 &&
                              !soldOut &&
                              isOnSale && (
                                <Text color="green.800" marginLeft="2xs">
                                  <CurrencyFormat
                                    value={getItemPrice(
                                      itemPrice,
                                      Boolean(hasAllInPricing),
                                      feesPricingInfo,
                                      paymentType,
                                      purchaseSourcePlatform,
                                      autoDiscount?.amount
                                    )}
                                    displayType="text"
                                    currency={currency}
                                    includeSuffix
                                  />
                                </Text>
                              )}
                          </Flex>
                        </Heading>
                        <AllInPricing
                          hasAllInPricing={Boolean(hasAllInPricing)}
                          feesPricingInfo={feesPricingInfo}
                          price={itemPrice}
                          currency={currency}
                          autoDiscountAmount={autoDiscount?.amount}
                        />
                        {itemPrice &&
                          Number(itemPrice) > 0 &&
                          (autoDiscount?.amount ?? 0) > 0 &&
                          autoDiscount?.description !== "" &&
                          !soldOut &&
                          isOnSale && (
                            <Text fontSize="xs" mt="xs" color="green.800">
                              {autoDiscount?.description} -
                              <CurrencyFormat
                                value={getItemPrice(
                                  (autoDiscount?.amount ?? 0).toString(),
                                  Boolean(hasAllInPricing) === false,
                                  feesPricingInfo,
                                  paymentType,
                                  purchaseSourcePlatform
                                )}
                                displayType="text"
                                currency={currency}
                                includeSuffix={false}
                              />
                              {t(" off per item applied at checkout")}
                            </Text>
                          )}
                        {minSelectionAmount > 1 && (
                          <Text fontSize="0.875rem" mt="xs">
                            {`*${t("min purchase of")} ${minSelectionAmount}`}
                          </Text>
                        )}
                      </Flex>

                      {/* Show quantity buttons/selection dropdown on same line as price */}
                      {showQuantityButtons && (
                        <Flex
                          data-testid={`${itemTitle}-quantity-select-same-line`}
                          className={bem.element("quantity-select-same-line")}
                          display={
                            alwaysBelow
                              ? "none"
                              : {
                                  base: "none",
                                  md: isCalendar ? "none" : "flex",
                                  lg: "flex",
                                }
                          }
                          alignItems="center"
                        >
                          <Box minWidth="element.2xl" />
                          {getIconDisplay()}
                          <Flex
                            width="element.9xl"
                            direction="row"
                            alignItems="flex-end"
                            justifyContent="flex-end"
                          >
                            {getQuantityButtonDisplay()}
                          </Flex>
                        </Flex>
                      )}
                    </Flex>

                    {children}

                    {/* Show quantity buttons/selection dropdown on a new line */}
                    {showQuantityButtons && (
                      <Flex
                        className={bem.element("quantity-select-below")}
                        data-testid={`${itemTitle}-quantity-select-below`}
                        display={
                          alwaysBelow
                            ? "flex"
                            : {
                                base: "flex",
                                md: isCalendar ? "flex" : "none",
                                lg: "none",
                              }
                        }
                        pt="lg"
                        width="100%"
                        direction="row"
                        alignItems="center"
                      >
                        {getIconDisplay()}
                        {getQuantityButtonDisplay()}
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ItemQuantitySelectInfo;
